import { Corner, Root, Scrollbar, Thumb, Viewport } from '@radix-ui/react-scroll-area';
import clsx from 'clsx';
import type { ComponentProps, FunctionComponent, PropsWithChildren, RefObject, UIEventHandler } from 'react';

import { type ScrollFadeOrientation, useScrollFade } from '../scroll-fade/ScrollFade';

export type ScrollAreaProps = {
    readonly className?: string;
    readonly orientation?: 'both' | 'horizontal' | 'vertical';
    readonly scrollHideDelay?: number;
    readonly scrollFade?: ScrollFadeOrientation;
    readonly scrollFadeSize?: string;
    readonly onViewportScroll?: UIEventHandler<HTMLDivElement>;
    readonly type?: 'always' | 'auto' | 'hover' | 'scroll';
    readonly viewportClassName?: string;
    readonly viewportRef?: RefObject<HTMLDivElement | null>;
    readonly viewportTestId?: string;
};

export const StyledScrollArea: FunctionComponent<ComponentProps<typeof Root>> = ({ className, ...props }) => (
    <Root className={clsx('flex h-full w-full', className)} {...props} />
);

export const StyledScrollAreaViewport: FunctionComponent<ComponentProps<typeof Viewport>> = ({
    className,
    ...props
}) => <Viewport className={clsx('h-full w-full outline-none', className)} data-scroll-area-viewport="" {...props} />;

export const StyledScrollAreaScrollbar: FunctionComponent<ComponentProps<typeof Scrollbar>> = ({
    className,
    ...props
}) => (
    <Scrollbar
        className={clsx(
            'flex bg-surface-hover rounded-sm select-none z-[2]',
            'transition-[background-color] duration-[160ms] ease-out',
            'hover:bg-surface-subtle',
            'data-[orientation=vertical]:flex-row data-[orientation=vertical]:h-full data-[orientation=vertical]:w-[3px]',
            'data-[orientation=horizontal]:flex-col data-[orientation=horizontal]:h-[3px] data-[orientation=horizontal]:w-full',
            className,
        )}
        {...props}
    />
);

export const StyledScrollAreaThumb: FunctionComponent<ComponentProps<typeof Thumb>> = ({ className, ...props }) => (
    <Thumb
        className={clsx(
            'flex-1 relative bg-border-strong rounded-[inherit]',
            'before:content-[""] before:absolute before:top-1/2 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2',
            'before:w-full before:h-full before:min-w-[2px] before:min-h-[2px]',
            'before:bg-inherit before:rounded-[inherit]',
            className,
        )}
        {...props}
    />
);

export const StyledScrollAreaCorner: FunctionComponent<ComponentProps<typeof Corner>> = ({ className, ...props }) => (
    <Corner className={clsx('bg-surface-subtle', className)} {...props} />
);

export const ScrollArea: FunctionComponent<PropsWithChildren<ScrollAreaProps>> = ({
    children,
    className,
    orientation = 'vertical',
    onViewportScroll,
    scrollFade,
    scrollFadeSize,
    scrollHideDelay = 200,
    type = 'hover',
    viewportClassName,
    viewportRef,
    viewportTestId,
    ...props
}) => {
    const showVertical = orientation === 'vertical' || orientation === 'both';
    const showHorizontal = orientation === 'horizontal' || orientation === 'both';
    const fadeOrientation = scrollFade ?? 'vertical';
    const fade = useScrollFade({
        disabled: scrollFade === undefined,
        edgeFadeSize: scrollFadeSize,
        orientation: fadeOrientation,
        viewportRef,
    });

    return (
        <StyledScrollArea className={className} scrollHideDelay={scrollHideDelay} type={type} {...props}>
            <StyledScrollAreaViewport
                ref={fade.viewportRef}
                className={viewportClassName}
                data-overflow-bottom={scrollFade === 'vertical' ? fade.overflowEdges.end : undefined}
                data-overflow-left={scrollFade === 'horizontal' ? fade.overflowEdges.start : undefined}
                data-overflow-right={scrollFade === 'horizontal' ? fade.overflowEdges.end : undefined}
                data-overflow-top={scrollFade === 'vertical' ? fade.overflowEdges.start : undefined}
                data-testid={viewportTestId}
                style={fade.style}
                onScroll={onViewportScroll}
            >
                {children}
            </StyledScrollAreaViewport>
            {showVertical && (
                <StyledScrollAreaScrollbar orientation="vertical">
                    <StyledScrollAreaThumb />
                </StyledScrollAreaScrollbar>
            )}
            {showHorizontal && (
                <StyledScrollAreaScrollbar orientation="horizontal">
                    <StyledScrollAreaThumb />
                </StyledScrollAreaScrollbar>
            )}
            <StyledScrollAreaCorner />
        </StyledScrollArea>
    );
};
